import { useQuery } from '@tanstack/react-query';
import { put } from '@/shared/api/api-client';

/**
 * Address autocomplete — backend proxy over Google Places Autocomplete.
 *
 * Wire (verified 2026-05-13):
 *   PUT /api/Master/GetAddress
 *   body: { input, types: 'establishment', language: 'en', key: <google-places-key> }
 *
 * Response: `{ predictions: GooglePlacesPrediction[] }` — forwarded as-is
 * from Google. The PUT verb is unusual for a "get" semantic but matches
 * the backend's contract (Flutter does the same).
 *
 * The Google Places API key is sent in the request body. It's intentionally
 * exposed because both the Flutter app and the dev-tools curl carry it in
 * plain text; the backend is the proxy, not the key holder. Treat this
 * as a public client key, not a secret.
 */

const GOOGLE_PLACES_KEY = 'AIzaSyBCrQ1GzILNdnaeDRUZbKzGc1CIICTIAXw';

export interface AddressTerm {
  offset: number;
  value: string;
}

export interface AddressPrediction {
  description: string;
  place_id: string;
  reference: string;
  structured_formatting?: {
    main_text?: string;
    secondary_text?: string;
  };
  terms?: AddressTerm[];
  types?: string[];
}

export interface AddressSuggestionsResponse {
  predictions?: AddressPrediction[];
}

interface UseAddressSuggestionsParams {
  /** Pre-debounced input — the consumer should pass a stable query (debounce upstream). */
  input: string;
  /** Disable the query while the input is empty or the picker is closed. */
  enabled?: boolean;
}

export function useAddressSuggestions({
  input,
  enabled = true,
}: UseAddressSuggestionsParams) {
  const q = input.trim();
  return useQuery({
    queryKey: ['address-suggestions', q],
    queryFn: ({ signal }) =>
      put<AddressSuggestionsResponse>(
        '/api/Master/GetAddress',
        {
          input: q,
          types: 'establishment',
          language: 'en',
          key: GOOGLE_PLACES_KEY,
        },
        { signal },
      ),
    enabled: enabled && q.length >= 2,
    // The Places API charges per request; cache aggressively per query
    // so retyping the same string doesn't re-fire.
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Build the display address from a prediction. Mirrors Flutter
 * `create_tpa_widget.dart:198-207`:
 *   `${main_text}, ${terms[n-2]}, ${terms[n-1]}`
 * Falls back to `description` when terms are short or `main_text` is
 * unavailable.
 */
export function formatPredictionAddress(p: AddressPrediction): string {
  const main = p.structured_formatting?.main_text;
  const terms = p.terms ?? [];
  if (main && terms.length >= 2) {
    const secondLast = terms[terms.length - 2]?.value;
    const last = terms[terms.length - 1]?.value;
    return [main, secondLast, last].filter(Boolean).join(', ');
  }
  return p.description ?? main ?? '';
}

/**
 * Derive the fields an address-pick writes to a form from a Places
 * prediction:
 *   - `mainText` = `structured_formatting.main_text` (fallback `description`)
 *   - `city`     = `terms[n-2]`  — Flutter `…:203-227`
 *   - `country`  = `terms[n-1]`
 *
 * Shared by the residential-address picker (`address-edit-fields.tsx`) and
 * the emergency-contact address picker (`emergency-contact-edit-fields.tsx`),
 * which had byte-identical copies of this parsing (Samer greenlit the
 * dedup). Empty parts come back as `''` so the caller can normalise to
 * `null` for the wire as it sees fit.
 */
export function deriveCityCountryFromTerms(p: AddressPrediction): {
  mainText: string;
  city: string;
  country: string;
} {
  const mainText = p.structured_formatting?.main_text ?? p.description ?? '';
  const terms = p.terms ?? [];
  const city = terms.length >= 2 ? (terms[terms.length - 2]?.value ?? '') : '';
  const country = terms.length >= 1 ? (terms[terms.length - 1]?.value ?? '') : '';
  return { mainText, city, country };
}
